const pending = new Set()
const same = (left, right) => JSON.stringify(left ?? '') === JSON.stringify(right ?? '')

// Flush before loading another journal view, so its GET cannot overtake the last edit.
export const waitForJournalSaves = () => Promise.all(Array.from(pending, draft => draft.flush().catch(() => undefined)))

export const createJournalDraft = ({ initial, draftKey, storage, save }) => {
  let value = initial
  let dirty = false
  let running
  let revision = 0
  let stored
  const listeners = new Set()
  const notify = () => listeners.forEach(listener => listener(value))
  try {
    stored = storage?.getItem(draftKey)
    const draft = JSON.parse(stored)
    if (draft && Object.prototype.hasOwnProperty.call(draft, 'value')) {
      value = draft.value
      dirty = !same(initial, value)
      if (!dirty && storage?.getItem(draftKey) === stored) storage.removeItem(draftKey)
    }
  } catch { /* HTTP saving still works when local storage is unavailable. */ }
  const persist = () => {
    try {
      stored = JSON.stringify({ value })
      storage?.setItem(draftKey, stored)
    } catch { /* Keep the in-memory draft until the server acknowledges it. */ }
  }
  const draft = {
    read: () => value,
    subscribe: listener => { listeners.add(listener); return () => listeners.delete(listener) },
    acceptServer: next => {
      if (dirty || running) return
      value = next
      notify()
    },
    change: next => {
      if (same(value, next)) return
      value = next
      revision += 1
      dirty = true
      pending.add(draft)
      persist()
      notify()
    },
    flush: () => {
      if (running) return running
      if (!dirty) return Promise.resolve()
      running = (async () => {
        while (dirty) {
          const sentRevision = revision
          const response = await save(value)
          if (!response || response.status >= 400 || response.data?.status === 'error') {
            throw new Error('Не удалось сохранить изменение журнала')
          }
          dirty = sentRevision !== revision
          if (!dirty) {
            try {
              if (storage?.getItem(draftKey) === stored) storage.removeItem(draftKey)
            } catch { /* A newer draft from another window must be kept. */ }
            pending.delete(draft)
          }
        }
      })().catch(error => { pending.delete(draft); throw error }).finally(() => { running = null })
      return running
    },
  }
  if (dirty) pending.add(draft)
  return draft
}

// The same cell shares its draft between the general journal, final grades and ДЗ/КР.
export const journalCellDraftKey = (field, fieldId, studentId, groupId) => {
  const prefix = field.is_journal_column ? 'final' : field.number ? `additional:${field.number}` : 'field'
  return `${prefix}:${studentId}:${groupId}:${field.column_id ?? fieldId}`
}
